import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  FormControl,
  FormLabel,
  RadioGroup,
  Radio,
  FormControlLabel,
  Checkbox,
  FormGroup,
  Button,
  Alert,
  CircularProgress,
  MenuItem,
  Select,
  InputLabel,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  Chip,
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Delete as DeleteIcon,
  PictureAsPdf as PdfIcon,
  Image as ImageIcon,
  InsertDriveFile as FileIcon,
} from '@mui/icons-material';
import { questionnaireService } from '../../services/questionnaireService';
import { documentService } from '../../services/documentService';
import { LocalDocument } from '../ProjectCreation/DocumentsStep';

interface Question {
  id: string;
  text: string;
  type: 'single-select' | 'multi-select' | 'number' | 'text';
  required: boolean;
  options?: Array<{ value: string; label: string; hasSubField?: boolean; subFieldType?: string }>;
  subText?: string;
  unit?: string;
}

interface BasicQuestionsProps {
  projectId: string;
  projectType: string;
  onComplete?: (responses: Record<string, any>, workflow?: any, sessionId?: string) => void;
  isGeneratingWorkflow?: boolean;
  workflowCompleted?: boolean;
}

const BasicQuestions: React.FC<BasicQuestionsProps> = ({ projectId, projectType, onComplete, isGeneratingWorkflow = false, workflowCompleted = false }) => {
  const [questions, setQuestions] = useState<Question[]>([]);
  const [responses, setResponses] = useState<Record<string, any>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [saving, setSaving] = useState(false);
  const [isReadOnly, setIsReadOnly] = useState(false); // New state for disabling inputs
  const [basicDocuments, setBasicDocuments] = useState<LocalDocument[]>([]);
  const [selectedDocType, setSelectedDocType] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string>('');

  useEffect(() => {
    loadBasicQuestions();
  }, [projectId, projectType]);

  const loadBasicQuestions = async () => {
    try {
      setLoading(true);
      const data = await questionnaireService.getBasicQuestions(projectId, projectType);
      
      // Set the questions configuration for rendering
      setQuestions(data.questions || []);

      if (data.existingResponses && typeof data.existingResponses === 'object') {
        // Data loaded from DB, enable read-only mode
        setIsReadOnly(true); 

        const parsedResponses: Record<string, any> = {};
        const responsesSource = data.existingResponses;

        // Iterate over the keys in the database object (e.g., 'loft_type', 'extension_rooms')
        Object.entries(responsesSource).forEach(([questionId, responseObj]: [string, any]) => {
          if (!responseObj) return;

          // 1. Handle Complex Multi-select (Loft or Extension Rooms)
          if ((questionId === 'loft_rooms' || questionId === 'extension_rooms') && responseObj.selections) {
            
            parsedResponses[questionId] = responseObj.selections.map((s: any) => s.id);
            
            if (responseObj.bedroom_number) {
              parsedResponses[`${questionId}_bedroom_number`] = responseObj.bedroom_number;
            }
            
            if (responseObj.other_description) {
              parsedResponses[`${questionId}_other_other`] = responseObj.other_description;
            }
          } 
          
          // 2. Handle Single Select Objects
          else if (typeof responseObj === 'object' && responseObj.id) {
            parsedResponses[questionId] = responseObj.id;
              // NEW: Check if there is a custom detail saved (e.g. the exact height)
              if (responseObj.detail) {
                // Map it back to the state key expected by the UI: questionId + "_other"
                parsedResponses[`${questionId}_other`] = responseObj.detail;
              }
          }

          // 3. Handle Simple Values
          else {
            parsedResponses[questionId] = responseObj;
          }
        });
        
//        console.log('Final Prepared UI State:', parsedResponses);
        setResponses(parsedResponses);
      }
    } catch (err: any) {
      console.error('Mapping Error:', err);
      setError('Failed to load project responses');
    } finally {
      setLoading(false);
    }
  };

  const handleSingleSelectChange = (questionId: string, value: string) => {
    setResponses(prev => ({
      ...prev,
      [questionId]: value
    }));
  };

  const handleMultiSelectChange = (questionId: string, value: string, checked: boolean) => {
    setResponses(prev => {
      const current = prev[questionId] || [];
      if (checked) {
        return { ...prev, [questionId]: [...current, value] };
      } else {
        return { ...prev, [questionId]: current.filter((v: string) => v !== value) };
      }
    });
  };

  const handleNumberChange = (questionId: string, value: string) => {
    setResponses(prev => ({
      ...prev,
      [questionId]: value
    }));
  };

  const handleTextChange = (questionId: string, value: string) => {
    setResponses(prev => ({
      ...prev,
      [questionId]: value
    }));
  };

  const handleSubFieldChange = (questionId: string, subFieldKey: string, value: string) => {
    setResponses(prev => ({
      ...prev,
      [`${questionId}_${subFieldKey}`]: value
    }));
  };

  const isQuestionAnswered = (question: Question): boolean => {
    const response = responses[question.id];
    if (!response) return false;
    if (question.type === 'multi-select') {
      return Array.isArray(response) && response.length > 0;
    }
    return !!response;
  };

  const canSubmit = (): boolean => {
    return questions.every(q => !q.required || isQuestionAnswered(q));
  };

  const handleSubmit = async () => {
    // If read only, we might trigger a specific flow or just prevent saving again
    if (isReadOnly && onComplete) {
        onComplete(responses); 
        return;
    }

    if (!canSubmit()) {
      setError('Please answer all required questions');
      return;
    }

    try {
      setSaving(true);
      setError('');

      // --- NEW LOGIC: Create a sanitized copy of responses ---
      const cleanedResponses: Record<string, any> = {};
      Object.keys(responses).forEach(key => {
        const value = responses[key];
        // If it's a string, trim it. Otherwise (arrays/numbers/null), keep as is.
        if (typeof value === 'string') {
          cleanedResponses[key] = value.trim();
        } else {
          cleanedResponses[key] = value;
        }
      });
      // -------------------------------------------------------

      const questionsWithAnswers = questions.map(question => {
        // USE cleanedResponses INSTEAD OF responses HERE
        const answerValue = cleanedResponses[question.id];
        
        if (!answerValue) return { id: question.id, text: question.text, answer: null };

        const questionData: any = { id: question.id, text: question.text, answer: null };

        if (question.type === 'multi-select' && Array.isArray(answerValue)) {
          const selectedOptions = answerValue.map(val => {
            const option = question.options?.find(opt => opt.value === val);
            return { id: val, label: option ? option.label : val };
          });

          if (question.id === 'loft_rooms' || question.id === 'extension_rooms') {
            questionData.answer = {
              selections: selectedOptions,
              // Use cleanedResponses for sub-fields too
              bedroom_number: cleanedResponses[`${question.id}_bedroom_number`] || null,
              other_description: cleanedResponses[`${question.id}_other_other`] || null
            };
          } else {
            questionData.answer = selectedOptions;
          }
        } else if (question.type === 'single-select') {
          const option = question.options?.find(opt => opt.value === answerValue);
          // Create the base answer object
          const answerObj: any = { 
            id: answerValue, 
            label: option ? option.label : answerValue 
          };

          // CHECK: Does this option have a sub-field (like the exact height)?
          if (option?.hasSubField) {
            // Use cleanedResponses to get the trimmed custom text
            const customText = cleanedResponses[`${question.id}_other`];
            if (customText) {
              answerObj.detail = customText; // Attach the custom text to the answer
            }
          }
          questionData.answer = answerObj;
        } else {
          // This handles 'text' and 'number' types (already trimmed by our loop above)
          questionData.answer = answerValue;
        }
        return questionData;
      });
      
      if (basicDocuments.length > 0) {
        questionsWithAnswers.push({
          id: 'additional_documents',
          text: `Would you like to upload any additional files for your ${projectType}?`,
          answer: basicDocuments
        });
      }

      // Pass the cleaned responses to the save function
      const result = await questionnaireService.saveBasicQuestions(projectId, { questionsWithAnswers });

      if (onComplete) {
        // Return cleaned responses to the parent component as well
        onComplete(cleanedResponses, null, result?.sessionId);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to save responses');
    } finally {
      setSaving(false);
    }
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0 || !selectedDocType) return;

    setUploading(true);
    setUploadError('');

    try {
      for (const file of files) {
        if (file.size > 50 * 1024 * 1024) {
          setUploadError(`File ${file.name} is too large. Maximum size is 50MB.`);
          continue;
        }

        await documentService.uploadDocument(projectId, file, selectedDocType);

        const newDoc: LocalDocument = {
          id: Math.random().toString(36).substr(2, 9),
          file,
          documentType: selectedDocType,
        };

        setBasicDocuments(prev => [...prev, newDoc]);
      }
    } catch (error) {
      setUploadError('Upload failed. Please try again.');
    } finally {
      setUploading(false);
      setSelectedDocType('');
      event.target.value = '';
    }
  };

  const removeBasicDocument = async (docId: string) => {
    try {
      await documentService.deleteDocument(docId);
      setBasicDocuments(prev => prev.filter(doc => doc.id !== docId));
    } catch (error) {
      console.error('Failed to delete document:', error);
    }
  };

  const getFileIcon = (fileName: string) => {
    const ext = fileName.split('.').pop()?.toLowerCase();
    switch (ext) {
      case 'pdf':
        return <PdfIcon color="error" />;
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif':
        return <ImageIcon color="primary" />;
      default:
        return <FileIcon color="action" />;
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error && questions.length === 0) {
    return (
      <Alert severity="error" sx={{ mb: 3 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {isReadOnly 
          ? "Answers have been loaded from the database."
          : "Please answer these questions to help us understand your project requirements."}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {questions.map((question, index) => (
        <Card key={question.id} sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="subtitle1" gutterBottom>
              {index + 1}. {question.text}
              {question.required && <span style={{ color: 'red' }}> *</span>}
            </Typography>

            {question.subText && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontStyle: 'italic' }}>
                {question.subText}
              </Typography>
            )}

            {question.type === 'single-select' && (
              <FormControl component="fieldset" fullWidth>
                <RadioGroup
                  value={responses[question.id] || ''}
                  onChange={(e) => handleSingleSelectChange(question.id, e.target.value)}
                >
                  {question.options?.map((option) => (
                    <Box key={option.value}>
                      <FormControlLabel
                        value={option.value}
                        control={<Radio disabled={isReadOnly} />}
                        label={option.label}
                      />
                      {option.hasSubField && responses[question.id] === option.value && (
                        <Box sx={{ ml: 4, mt: 1, mb: 2 }}>
                          {option.subFieldType === 'dropdown' ? (
                            <FormControl size="small" sx={{ minWidth: 120 }}>
                              <InputLabel>Number</InputLabel>
                              <Select
                                value={responses[`${question.id}_number`] || ''}
                                onChange={(e) => handleSubFieldChange(question.id, 'number', e.target.value)}
                                label="Number"
                                disabled={isReadOnly}
                              >
                                <MenuItem value="1">1</MenuItem>
                                <MenuItem value="2">2</MenuItem>
                                <MenuItem value="3">3</MenuItem>
                              </Select>
                            </FormControl>
                          ) : (
                            <TextField
                              size="small"
                              placeholder="Please specify"
                              value={responses[`${question.id}_other`] || ''}
                              onChange={(e) => handleSubFieldChange(question.id, 'other', e.target.value)}
                              fullWidth
                              disabled={isReadOnly}
                              required={true}
                            />
                          )}
                        </Box>
                      )}
                    </Box>
                  ))}
                </RadioGroup>
              </FormControl>
            )}

            {question.type === 'multi-select' && (
              <FormControl component="fieldset" fullWidth>
                <FormGroup>
                  {question.options?.map((option) => (
                    <Box key={option.value}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={(responses[question.id] || []).includes(option.value)}
                            onChange={(e) => handleMultiSelectChange(question.id, option.value, e.target.checked)}
                            disabled={isReadOnly}
                          />
                        }
                        label={option.label}
                      />
                      {option.hasSubField && (responses[question.id] || []).includes(option.value) && (
                        <Box sx={{ ml: 4, mt: 1, mb: 2 }}>
                          {option.subFieldType === 'dropdown' ? (
                            <FormControl size="small" sx={{ minWidth: 120 }}>
                              <InputLabel>Number</InputLabel>
                              <Select
                                value={responses[`${question.id}_${option.value}_number`] || ''}
                                onChange={(e) => handleSubFieldChange(question.id, `${option.value}_number`, e.target.value)}
                                label="Number"
                                disabled={isReadOnly}
                              >
                                <MenuItem value="1">1</MenuItem>
                                <MenuItem value="2">2</MenuItem>
                                <MenuItem value="3">3</MenuItem>
                              </Select>
                            </FormControl>
                          ) : (
                            <TextField
                              size="small"
                              placeholder="Please specify"
                              value={responses[`${question.id}_${option.value}_other`] || ''}
                              onChange={(e) => handleSubFieldChange(question.id, `${option.value}_other`, e.target.value)}
                              fullWidth
                              disabled={isReadOnly}
                            />
                          )}
                        </Box>
                      )}
                    </Box>
                  ))}
                </FormGroup>
              </FormControl>
            )}

            {question.type === 'number' && (
              <TextField
                type="number"
                fullWidth
                value={responses[question.id] || ''}
                onChange={(e) => handleNumberChange(question.id, e.target.value)}
                InputProps={{
                  endAdornment: question.unit ? <Typography variant="body2">{question.unit}</Typography> : null
                }}
                disabled={isReadOnly}
              />
            )}

            {question.type === 'text' && (
              <TextField
                fullWidth
                multiline
                rows={3}
                value={responses[question.id] || ''}
                onChange={(e) => handleTextChange(question.id, e.target.value)}
                disabled={isReadOnly}
              />
            )}
          </CardContent>
        </Card>
      ))}

      {/* File Upload Question */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="subtitle1" gutterBottom>
            {questions.length + 1}. Would you like to upload any additional files?
          </Typography>
          
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Document Type</InputLabel>
                <Select
                  value={selectedDocType}
                  onChange={(e) => setSelectedDocType(e.target.value)}
                  label="Document Type"
                  disabled={isReadOnly}
                >
                  {['Planning Application', 'Building Regulations', 'Structural Calculations', 'Architectural Drawings', 'Site Survey', 'Photos/Images', 'Other'].map((type) => (
                    <MenuItem key={type} value={type}>
                      {type}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {uploadError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {uploadError}
                </Alert>
              )}

              <Button
                variant="outlined"
                component="label"
                startIcon={uploading ? <CircularProgress size={20} /> : <UploadIcon />}
                fullWidth
                disabled={!selectedDocType || uploading || isReadOnly}
              >
                {uploading ? 'Uploading...' : 'Choose Files'}
                <input
                  type="file"
                  hidden
                  multiple
                  accept=".pdf,.doc,.docx,.ppt,.pptx,.jpg,.jpeg,.png,.gif,.dwg,.dxf,.skp,.3dm,.rvt"
                  onChange={handleFileUpload}
                  disabled={isReadOnly}
                />
              </Button>
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" gutterBottom>
                Selected Files ({basicDocuments.length})
              </Typography>
              {basicDocuments.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                  No files selected
                </Typography>
              ) : (
                <List dense>
                  {basicDocuments.map((doc) => (
                    <ListItem key={doc.id} sx={{ px: 0 }}>
                      <ListItemIcon>
                        {getFileIcon(doc.file.name)}
                      </ListItemIcon>
                      <ListItemText
                        primary={doc.file.name}
                        secondary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Chip label={doc.documentType} size="small" variant="outlined" />
                            <Typography variant="caption" color="text.secondary">
                              {formatFileSize(doc.file.size)}
                            </Typography>
                          </Box>
                        }
                      />
                      <IconButton
                        edge="end"
                        onClick={() => removeBasicDocument(doc.id)}
                        size="small"
                        disabled={isReadOnly}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </ListItem>
                  ))}
                </List>
              )}
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
        <Button
          variant="contained"
          size="large"
          onClick={handleSubmit}
          disabled={(!canSubmit() && !isReadOnly) || saving || isGeneratingWorkflow || workflowCompleted || isReadOnly}
        >
          {saving || isGeneratingWorkflow ? (
            <CircularProgress size={24} />
          ) : workflowCompleted ? (
            '✓ Workflow Generated'
          ) : isReadOnly ? (
            'Saved'
          ) : (
            'Start the AI Magic'
          )}
        </Button>
      </Box>
    </Box>
  );
};

export default BasicQuestions;